package or.garden.manager.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import or.garden.manager.domain.model.BedData
import or.garden.manager.domain.model.NaturalFertilizer
import or.garden.manager.domain.model.FertilizerType
import or.garden.manager.features.fertilizer.StatusBadge
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.*

@Composable
fun IrrigationModal(
    beds: List<BedData>,
    selectedBedId: Int?,
    onBedIdChange: (Int?) -> Unit,
    selectedDate: String,
    onDateChange: (String) -> Unit,
    selectedFrequency: String,
    onFrequencyChange: (String) -> Unit,
    isSaving: Boolean,
    onSave: () -> Unit,
    onClose: () -> Unit
) {
    Div({
        classes("fixed", "inset-0", "bg-black/50", "flex", "items-center", "justify-center", "z-50", "p-4")
        onClick { onClose() }
    }) {
        Div({
            classes("bg-white", "rounded-3xl", "max-w-md", "w-full", "p-8", "shadow-2xl")
            onClick { it.stopPropagation() }
        }) {
            H2({ classes("text-2xl", "font-bold", "text-gray-900", "mb-6") }) { Text("Novi raspored navodnjavanja") }

            Div({ classes("space-y-4") }) {
                Div {
                    Label(attrs = { classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1") }) { Text("Izaberi leju") }
                    Select({
                        classes("w-full", "p-3", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-blue-500", "outline-none")
                        onInput { onBedIdChange(it.value?.toIntOrNull()) }
                    }) {
                        Option("") { Text("Izaberi leju...") }
                        beds.forEach { bed ->
                            Option(bed.id.toString(), attrs = {
                                if (bed.id == selectedBedId) selected()
                            }) { Text(bed.name) }
                        }
                    }
                }

                Div {
                    Label(attrs = { classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1") }) { Text("Datum početka") }
                    Input(InputType.Date) {
                        classes("w-full", "p-3", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-blue-500", "outline-none")
                        value(selectedDate)
                        onInput { onDateChange(it.value) }
                    }
                }

                Div {
                    Label(attrs = { classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1") }) { Text("Frekvencija") }
                    Select({
                        classes("w-full", "p-3", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-blue-500", "outline-none")
                        onInput { onFrequencyChange(it.value ?: "daily") }
                    }) {
                        Option("daily", attrs = { if (selectedFrequency == "daily") selected() }) { Text("Svaki dan") }
                        Option("every_2_days", attrs = { if (selectedFrequency == "every_2_days") selected() }) { Text("Svaka 2 dana") }
                        Option("every_3_days", attrs = { if (selectedFrequency == "every_3_days") selected() }) { Text("Svaka 3 dana") }
                        Option("weekly", attrs = { if (selectedFrequency == "weekly") selected() }) { Text("Sedmično") }
                        Option("biweekly", attrs = { if (selectedFrequency == "biweekly") selected() }) { Text("Svake 2 sedmice") }
                        Option("monthly", attrs = { if (selectedFrequency == "monthly") selected() }) { Text("Mjesečno") }
                    }
                }
            }

            Div({ classes("flex", "gap-3", "mt-8") }) {
                Button({
                    classes("flex-1", "py-3", "px-4", "border", "border-gray-200", "rounded-xl", "text-gray-600", "font-bold", "hover:bg-gray-50")
                    onClick { onClose() }
                }) { Text("Otkaži") }

                Button({
                    val isFormValid = selectedBedId != null && selectedDate.isNotBlank()
                    val btnClasses = mutableListOf("flex-1", "py-3", "px-4", "bg-blue-600", "text-white", "rounded-xl", "font-bold", "hover:bg-blue-700", "transition-all")
                    if (isSaving || !isFormValid) btnClasses.add("opacity-50")
                    classes(btnClasses)
                    onClick { if (isFormValid) onSave() }
                    if (isSaving || !isFormValid) disabled()
                }) {
                    Text(if (isSaving) "Spremanje..." else "Sačuvaj")
                }
            }
        }
    }
}

@Composable
fun BedModal(
    selectedBedForEdit: BedData?,
    bedName: String,
    onBedNameChange: (String) -> Unit,
    bedType: String,
    onBedTypeChange: (String) -> Unit,
    bedWidth: String,
    onBedWidthChange: (String) -> Unit,
    bedLength: String,
    onBedLengthChange: (String) -> Unit,
    bedOrientation: String,
    onBedOrientationChange: (String) -> Unit,
    isSaving: Boolean,
    onSave: () -> Unit,
    onClose: () -> Unit
) {
    Div({
        classes("fixed", "inset-0", "bg-black/50", "flex", "items-center", "justify-center", "z-50", "p-4")
        onClick { onClose() }
    }) {
        Div({
            classes("bg-white", "rounded-3xl", "max-w-md", "w-full", "p-8", "shadow-2xl")
            onClick { it.stopPropagation() }
        }) {
            H2({ classes("text-2xl", "font-bold", "text-gray-900", "mb-6") }) {
                Text(if (selectedBedForEdit == null) "Nova leja" else "Podešavanja leje")
            }

            Div({ classes("space-y-4") }) {
                Div {
                    Label(attrs = { classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1") }) { Text("Naziv leje") }
                    Input(InputType.Text) {
                        classes("w-full", "p-3", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none")
                        value(bedName)
                        onInput { onBedNameChange(it.value) }
                        placeholder("npr. Sjeverna gredica")
                    }
                }

                Div {
                    Label(attrs = { classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1") }) { Text("Tip leje") }
                    Select({
                        classes("w-full", "p-3", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none")
                        onInput { onBedTypeChange(it.value ?: "Raised Bed") }
                    }) {
                        mapOf(
                            "Raised Bed" to "Povišena leja",
                            "Ground Level" to "Na tlu",
                            "Container" to "Saksija/Posuda",
                            "Greenhouse" to "Plastenik",
                            "Permaculture Bed" to "Permakulturna leja",
                            "Planter Box" to "Sanduk za sadnju"
                        ).forEach { (value, label) ->
                            Option(value, attrs = {
                                if (value == bedType) selected()
                            }) { Text(label) }
                        }
                    }
                }

                Div {
                    Label(attrs = { classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1") }) { Text("Orijentacija") }
                    Select({
                        classes("w-full", "p-3", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none")
                        onInput { onBedOrientationChange(it.value ?: "0") }
                    }) {
                        mapOf(
                            "0" to "Istok-Zapad",
                            "1" to "Sjever-Jug"
                        ).forEach { (value, label) ->
                            Option(value, attrs = {
                                if (value == bedOrientation) selected()
                            }) { Text(label) }
                        }
                    }
                }

                Div({ classes("grid", "grid-cols-2", "gap-4") }) {
                    Div {
                        Label(attrs = { classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1") }) { Text("Širina (cm)") }
                        Input(InputType.Number) {
                            classes("w-full", "p-3", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none")
                            value(bedWidth)
                            onInput { onBedWidthChange(it.value.toString()) }
                        }
                    }
                    Div {
                        Label(attrs = { classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1") }) { Text("Dužina (cm)") }
                        Input(InputType.Number) {
                            classes("w-full", "p-3", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none")
                            value(bedLength)
                            onInput { onBedLengthChange(it.value.toString()) }
                        }
                    }
                }
            }

            Div({ classes("flex", "gap-3", "mt-8") }) {
                Button({
                    classes("flex-1", "py-3", "px-4", "border", "border-gray-200", "rounded-xl", "text-gray-600", "font-bold", "hover:bg-gray-50")
                    onClick { onClose() }
                }) { Text("Otkaži") }

                Button({
                    val isFormValid = bedName.isNotBlank() && bedWidth.isNotBlank() && bedLength.isNotBlank()
                    val btnClasses = mutableListOf("flex-1", "py-3", "px-4", "bg-emerald-600", "text-white", "rounded-xl", "font-bold", "hover:bg-emerald-700", "transition-all")
                    if (isSaving || !isFormValid) btnClasses.add("opacity-50")
                    classes(btnClasses)
                    onClick { if (isFormValid) onSave() }
                    if (isSaving || !isFormValid) disabled()
                }) {
                    Text(if (isSaving) "Spremanje..." else "Sačuvaj")
                }
            }
        }
    }
}

@Composable
fun ConfirmDeleteModal(
    title: String,
    message: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    Div({
        classes("fixed", "inset-0", "bg-black/50", "flex", "items-center", "justify-center", "z-[60]", "p-4")
        onClick { onCancel() }
    }) {
        Div({
            classes("bg-white", "rounded-3xl", "max-w-sm", "w-full", "p-8", "shadow-2xl", "text-center")
            onClick { it.stopPropagation() }
        }) {
            Div({
                classes("w-20", "h-20", "bg-red-50", "rounded-full", "flex", "items-center", "justify-center", "mx-auto", "mb-6")
            }) {
                I({ classes("fas", "fa-exclamation-triangle", "text-red-500", "text-3xl") })
            }

            H2({ classes("text-2xl", "font-bold", "text-gray-900", "mb-2") }) { Text(title) }
            P({ classes("text-gray-500", "mb-8") }) { Text(message) }

            Div({ classes("flex", "gap-3") }) {
                Button({
                    classes("flex-1", "py-3", "px-4", "border", "border-gray-200", "rounded-xl", "text-gray-600", "font-bold", "hover:bg-gray-50")
                    onClick { onCancel() }
                }) { Text("Odustani") }

                Button({
                    classes("flex-1", "py-3", "px-4", "bg-red-600", "text-white", "rounded-xl", "font-bold", "hover:bg-red-700", "transition-all")
                    onClick { onConfirm() }
                }) { Text("Obriši") }
            }
        }
    }
}

@Composable
fun SuccessModal(
    title: String,
    message: String,
    onClose: () -> Unit
) {
    Div({
        classes("fixed", "inset-0", "bg-black/50", "flex", "items-center", "justify-center", "z-[110]", "p-4")
        onClick { onClose() }
    }) {
        Div({
            classes("bg-white", "rounded-3xl", "max-w-sm", "w-full", "p-8", "shadow-2xl", "text-center")
            onClick { it.stopPropagation() }
        }) {
            Div({
                classes("w-20", "h-20", "bg-emerald-50", "rounded-full", "flex", "items-center", "justify-center", "mx-auto", "mb-6")
            }) {
                I({ classes("fas", "fa-check-circle", "text-emerald-500", "text-4xl") })
            }

            H2({ classes("text-2xl", "font-bold", "text-gray-900", "mb-2") }) { Text(title) }
            P({ classes("text-gray-500", "mb-8") }) { Text(message) }

            Button({
                classes("w-full", "py-3", "px-4", "bg-emerald-600", "text-white", "rounded-xl", "font-bold", "hover:bg-emerald-700", "transition-all")
                onClick { onClose() }
            }) { Text("U redu") }
        }
    }
}

@Composable
fun FertilizerDetailsModal(
    fertilizer: NaturalFertilizer,
    showEditButton: Boolean = false,
    onEdit: () -> Unit = {},
    onDelete: () -> Unit = {},
    onClose: () -> Unit
) {
    var showDeleteConfirm by remember { mutableStateOf(false) }

    Div({
        classes("fixed", "inset-0", "bg-black/50", "flex", "items-center", "justify-center", "z-50", "p-4")
        onClick { onClose() }
    }) {
        Div({
            classes("bg-white", "rounded-3xl", "max-w-2xl", "w-full", "max-h-[90vh]", "overflow-y-auto", "p-8", "shadow-2xl", "relative")
            onClick { it.stopPropagation() }
        }) {
            // Close Button
            Button({
                classes("absolute", "top-6", "right-6", "text-gray-400", "hover:text-gray-600", "transition-colors")
                onClick { onClose() }
            }) {
                I({ classes("fas", "fa-times", "text-xl") })
            }

            // Header
            Div({ classes("mb-8", "mt-6") }) {
                Div({ classes("flex", "items-center", "justify-between", "mb-4") }) {
                    Div({ classes("flex", "items-center", "gap-4") }) {
                        Div({
                            classes(
                                "w-12",
                                "h-12",
                                "rounded-xl",
                                "bg-emerald-50",
                                "flex",
                                "items-center",
                                "justify-center",
                                "text-emerald-600"
                            )
                        }) {
                            I({ classes("fas", "fa-seedling") })
                        }
                        StatusBadge(fertilizer.status)
                    }

                    if (showEditButton) {
                        Div({ classes("flex", "items-center", "gap-3") }) {
                            Button({
                                classes("p-2", "text-red-400", "hover:text-red-600", "transition-colors")
                                onClick { showDeleteConfirm = true }
                            }) {
                                I({ classes("fas", "fa-trash") })
                            }
                            Button({
                                classes("px-4", "py-2", "bg-emerald-50", "text-emerald-700", "rounded-xl", "font-bold", "hover:bg-emerald-100", "transition-all", "flex", "items-center", "gap-2")
                                onClick { onEdit() }
                            }) {
                                I({ classes("fas", "fa-edit") })
                                Text("Izmijeni")
                            }
                        }
                    }
                }
                H2({ classes("text-3xl", "font-bold", "text-[#134130]") }) { Text(fertilizer.name) }
                Div({ classes("flex", "items-center", "gap-2", "mt-2", "text-gray-500") }) {
                    I({ classes("fas", "fa-clock") })
                    val typeText = when (fertilizer.type) {
                        FertilizerType.FERMENTATION -> "Fermentacija (14 dana)"
                        FertilizerType.TEA -> "Biljni čaj (24h)"
                        FertilizerType.CHEMICAL_REACTION -> "Hemijska reakcija"
                    }
                    Text(typeText)
                }
            }

            // Content
            Div({ classes("space-y-8") }) {
                // Ingredients
                Div {
                    H3({ classes("text-sm", "font-bold", "text-gray-500", "uppercase", "tracking-wider", "mb-3") }) {
                        Text("Sastojci")
                    }
                    Div({ classes("text-gray-700", "leading-relaxed", "whitespace-pre-wrap") }) {
                        MarkdownText(fertilizer.ingredients ?: "Nema navedenih sastojaka.")
                    }
                }

                // Instructions
                Div {
                    H3({ classes("text-sm", "font-bold", "text-gray-500", "uppercase", "tracking-wider", "mb-3") }) {
                        Text("Kako se pravi i koristi")
                    }
                    Div({ classes("text-gray-700", "leading-relaxed", "whitespace-pre-wrap") }) {
                        MarkdownText(fertilizer.notes ?: "Nema dodatnih napomena.")
                    }
                }

                // Dilution Ratio
                if (fertilizer.dilutionRatio != null) {
                    Div({ classes("p-6", "bg-emerald-50", "rounded-2xl", "border", "border-emerald-100") }) {
                        H3({ classes("text-sm", "font-bold", "text-emerald-800", "uppercase", "tracking-wider", "mb-2") }) {
                            Text("Omjer razblaživanja")
                        }
                        Div({ classes("text-2xl", "font-bold", "text-emerald-600") }) {
                            Text(fertilizer.dilutionRatio ?: "-")
                        }
                    }
                }
            }
        }
    }

    if (showDeleteConfirm) {
        ConfirmDeleteModal(
            title = "Obriši đubrivo",
            message = "Da li ste sigurni da želite obrisati đubrivo '${fertilizer.name}'?",
            onConfirm = {
                onDelete()
                showDeleteConfirm = false
            },
            onCancel = { showDeleteConfirm = false }
        )
    }
}

@Composable
fun MarkdownText(text: String) {
    val regex = Regex("\\*\\*(.*?)\\*\\*")
    var lastIndex = 0
    val matches = regex.findAll(text)

    if (matches.none()) {
        Text(text)
        return
    }

    matches.forEach { matchResult ->
        // Text before match
        Text(text.substring(lastIndex, matchResult.range.first))
        // Bold text
        Span({ classes("font-bold") }) {
            Text(matchResult.groupValues[1])
        }
        lastIndex = matchResult.range.last + 1
    }
    // Remaining text
    Text(text.substring(lastIndex))
}
